import { WEATHER_TILE_CHANNELS } from "@nova/constants"
import { Layer, project32 } from "@deck.gl/core"
import type { LayerContext, LayerProps } from "@deck.gl/core"
import type { Texture } from "@luma.gl/core"
import { Geometry, Model } from "@luma.gl/engine"
import { ColorStop, buildColormapGlsl } from "../shared/colormap"
import { FrameImage, FrameTextureCache } from "../shared/frameTextureCache"
import { HEATMAP_QUAD_POSITIONS, HEATMAP_QUAD_VERTEX_COUNT } from "../shared/heatmapGeometry"

export const PRECIP_RAIN_COLOR_STOPS: ReadonlyArray<ColorStop> = [
    { value: 0, color: [0.55, 0.85, 0.55] },
    { value: 1, color: [0.2, 0.65, 0.3] },
    { value: 2.5, color: [0.95, 0.95, 0.3] },
    { value: 7.5, color: [0.95, 0.55, 0.15] },
    { value: 15, color: [0.85, 0.2, 0.2] },
    { value: 30, color: [0.65, 0.15, 0.55] },
    { value: 50, color: [0.45, 0.2, 0.65] },
]

export const PRECIP_HEATMAP_FADE_IN_MAX_MM = 0.2

export const PRECIP_HEATMAP_MAX_MM =
    PRECIP_RAIN_COLOR_STOPS[PRECIP_RAIN_COLOR_STOPS.length - 1].value

const MM_FULL_SCALE = WEATHER_TILE_CHANNELS.precipitation.mmPerHour.max

const HEATMAP_VS = `#version 300 es
in vec2 positions;
out vec2 v_normPos;

uniform vec4 u_bounds;

void main() {
    v_normPos = positions * 0.5 + 0.5;
    float lon = mix(u_bounds.x, u_bounds.z, v_normPos.x);
    float lat = mix(u_bounds.y, u_bounds.w, v_normPos.y);
    gl_Position = project_position_to_clipspace(vec3(lon, lat, 0.0), vec3(0.0), vec3(0.0));
}
`

const HEATMAP_FS = `#version 300 es
precision highp float;
precision highp sampler2D;

in vec2 v_normPos;

uniform sampler2D u_precipTextureA;
uniform sampler2D u_precipTextureB;
uniform float u_imageWeight;
uniform float u_fadeInMaxMm;
uniform float u_opacity;
uniform float u_mmFullScale;
uniform vec4 u_bounds;
uniform float u_glyphsPerDegree;
uniform vec3 u_glyphColor;
uniform float u_glyphOpacity;

out vec4 fragColor;

${buildColormapGlsl(PRECIP_RAIN_COLOR_STOPS, "rainColormap")}

const int TYPE_RAIN = 0;
const int TYPE_SNOW = 1;
const int TYPE_SLEET = 2;
const int TYPE_FREEZING = 3;

vec4 sampleLinear(sampler2D tex, vec2 normPos) {
    return texture(tex, vec2(normPos.x, 1.0 - normPos.y));
}

// Type code is categorical — sample at the texel center so hardware filtering can't blend codes.
float sampleTypeNearest(sampler2D tex, vec2 normPos) {
    vec2 dims = vec2(textureSize(tex, 0));
    vec2 sampleNorm = vec2(normPos.x, 1.0 - normPos.y);
    vec2 texel = (floor(sampleNorm * dims) + 0.5) / dims;
    return texture(tex, texel).g;
}

const float GLYPH_RADIUS = 0.42;
const float GLYPH_THICKNESS = 0.06;
const int GLYPH_FADE_CELLS = 1;

float starGlyph(vec2 p) {
    float r = length(p);
    float d0 = abs(p.y);
    float d1 = abs(0.5 * p.y - 0.8660254 * p.x);
    float d2 = abs(0.5 * p.y + 0.8660254 * p.x);
    float d = min(d0, min(d1, d2));
    float arm = 1.0 - smoothstep(GLYPH_THICKNESS * 0.5, GLYPH_THICKNESS, d);
    float disk = 1.0 - smoothstep(GLYPH_RADIUS * 0.75, GLYPH_RADIUS, r);
    return arm * disk;
}

float dotGlyph(vec2 p) {
    return 1.0 - smoothstep(0.16, 0.16 + GLYPH_THICKNESS, length(p));
}

float ringGlyph(vec2 p) {
    float band = abs(length(p) - 0.26);
    return 1.0 - smoothstep(GLYPH_THICKNESS * 0.5, GLYPH_THICKNESS, band);
}

float glyphMask(int typeCode, vec2 cellLocal) {
    if (typeCode == TYPE_SNOW) return starGlyph(cellLocal);
    if (typeCode == TYPE_SLEET) return dotGlyph(cellLocal);
    if (typeCode == TYPE_FREEZING) return ringGlyph(cellLocal);
    return 0.0;
}

// Glyph cells are laid out in Mercator space; the textures are linear in latitude.
vec2 cellCenterNormPos(vec2 cellIndex) {
    vec2 center = (cellIndex + 0.5) / u_glyphsPerDegree;
    float lat = 2.0 * (degrees(atan(exp(radians(center.y)))) - 45.0);
    return vec2(
        (center.x - u_bounds.x) / (u_bounds.z - u_bounds.x),
        (lat - u_bounds.y) / (u_bounds.w - u_bounds.y)
    );
}

// One type per cell, sampled at its centre, so no glyph is sliced by a type boundary.
int typeAtCell(vec2 cellIndex) {
    vec2 normPos = cellCenterNormPos(cellIndex);
    float typeF = u_imageWeight < 0.5
        ? sampleTypeNearest(u_precipTextureA, normPos)
        : sampleTypeNearest(u_precipTextureB, normPos);
    return int(typeF * 255.0 + 0.5);
}

// Share of neighbouring cells with the same type — ramps glyph opacity down at the edge of a region.
float cellTypeCoherence(vec2 cellIndex, int typeCode) {
    if (typeCode == TYPE_RAIN) return 0.0;

    float matches = 0.0;
    for (int dy = -GLYPH_FADE_CELLS; dy <= GLYPH_FADE_CELLS; dy++) {
        for (int dx = -GLYPH_FADE_CELLS; dx <= GLYPH_FADE_CELLS; dx++) {
            matches += typeAtCell(cellIndex + vec2(dx, dy)) == typeCode ? 1.0 : 0.0;
        }
    }
    float span = float(2 * GLYPH_FADE_CELLS + 1);
    return smoothstep(0.15, 0.85, matches / (span * span));
}

void main() {
    vec4 texA = sampleLinear(u_precipTextureA, v_normPos);
    vec4 texB = sampleLinear(u_precipTextureB, v_normPos);
    float intensity = mix(texA.r, texB.r, u_imageWeight) * u_mmFullScale;
    float coverage = mix(texA.a, texB.a, u_imageWeight);

    float alphaScale = smoothstep(0.0, u_fadeInMaxMm, intensity) * coverage;

    vec3 color = rainColormap(intensity);

    float lon = mix(u_bounds.x, u_bounds.z, v_normPos.x);
    float lat = mix(u_bounds.y, u_bounds.w, v_normPos.y);
    float latC = clamp(lat, -85.0, 85.0);
    float mercY = degrees(log(tan(radians(45.0 + latC * 0.5))));

    vec2 cellPos = vec2(lon, mercY) * u_glyphsPerDegree;
    vec2 cellIndex = floor(cellPos);
    vec2 cellLocal = cellPos - cellIndex - 0.5;
    int typeCode = typeAtCell(cellIndex);
    float glyph = glyphMask(typeCode, cellLocal) * alphaScale * cellTypeCoherence(cellIndex, typeCode);
    color = mix(color, u_glyphColor, glyph * u_glyphOpacity);

    fragColor = vec4(color, u_opacity * alphaScale);
}
`

export type PrecipitationHeatmapLayerProps = LayerProps & {
    imageA: FrameImage
    imageB: FrameImage
    imageWeight: number
    bounds: [number, number, number, number]
    fadeInMaxMm?: number
    opacity?: number
    glyphsPerDegree?: number
    glyphColor?: [number, number, number]
    glyphOpacity?: number
}

const DEFAULT_FADE_IN_MAX_MM = PRECIP_HEATMAP_FADE_IN_MAX_MM
const DEFAULT_OPACITY = 0.55
const DEFAULT_GLYPHS_PER_DEGREE = 6
const DEFAULT_GLYPH_COLOR = [1, 1, 1]
const DEFAULT_GLYPH_OPACITY = 0.6

export class PrecipitationHeatmapLayer extends Layer<PrecipitationHeatmapLayerProps> {
    static layerName = "PrecipitationHeatmapLayer"
    static defaultProps = {
        imageWeight: { type: "number", value: 0, min: 0, max: 1 },
        bounds: { type: "array", value: [-180, -90, 180, 90], compare: true },
        fadeInMaxMm: { type: "number", value: DEFAULT_FADE_IN_MAX_MM, min: 0 },
        opacity: { type: "number", value: DEFAULT_OPACITY, min: 0, max: 1 },
        glyphsPerDegree: { type: "number", value: DEFAULT_GLYPHS_PER_DEGREE, min: 0 },
        glyphColor: { type: "array", value: DEFAULT_GLYPH_COLOR, compare: true },
        glyphOpacity: { type: "number", value: DEFAULT_GLYPH_OPACITY, min: 0, max: 1 },
        parameters: { depthTest: false },
    }

    declare state: {
        textureCache: FrameTextureCache
        model: Model | null
    }

    getModels() {
        return this.state?.model ? [this.state.model] : []
    }

    initializeState(context: LayerContext): void {
        const { device } = context

        const shaders = this.getShaders({
            vs: HEATMAP_VS,
            fs: HEATMAP_FS,
            modules: [project32],
        })
        const model = new Model(device, {
            id: "precipitation-heatmap",
            ...shaders,
            geometry: new Geometry({
                topology: "triangle-strip",
                attributes: {
                    positions: { size: 2, value: HEATMAP_QUAD_POSITIONS },
                },
            }),
            vertexCount: HEATMAP_QUAD_VERTEX_COUNT,
            parameters: {
                blendColorSrcFactor: "src-alpha",
                blendColorDstFactor: "one-minus-src-alpha",
                blendAlphaSrcFactor: "one",
                blendAlphaDstFactor: "one-minus-src-alpha",
            },
        })

        this.setState({ textureCache: new FrameTextureCache(), model })
    }

    finalizeState(context: LayerContext): void {
        this.state.textureCache?.destroy()
        this.state.model?.destroy()
        super.finalizeState(context)
    }

    draw(): void {
        const { model, textureCache } = this.state
        const { imageA, imageB } = this.props
        if (!model || !imageA || imageA.width === 0 || imageA.height === 0) return

        const create = (image: FrameImage): Texture =>
            this.context.device.createTexture({
                width: image.width,
                height: image.height,
                format: "rgba8unorm",
                data: image.bitmap,
                sampler: { magFilter: "linear", minFilter: "linear" },
            })

        model.setBindings({
            u_precipTextureA: textureCache.get(imageA, create),
            u_precipTextureB: textureCache.get(imageB, create),
        })
        model.setUniforms({
            u_bounds: this.props.bounds,
            u_imageWeight: this.props.imageWeight ?? 0,
            u_fadeInMaxMm: this.props.fadeInMaxMm ?? DEFAULT_FADE_IN_MAX_MM,
            u_opacity: this.props.opacity ?? DEFAULT_OPACITY,
            u_mmFullScale: MM_FULL_SCALE,
            u_glyphsPerDegree: this.props.glyphsPerDegree ?? DEFAULT_GLYPHS_PER_DEGREE,
            u_glyphColor: this.props.glyphColor ?? DEFAULT_GLYPH_COLOR,
            u_glyphOpacity: this.props.glyphOpacity ?? DEFAULT_GLYPH_OPACITY,
        })
        model.draw(this.context.renderPass)
    }
}
